// @flow
// $FlowFixMe
import React from "react";
import { noop } from "lodash";
import type { BinaryFn, NullaryFn, UnaryFn } from "@tvg/types/Functional";
import type { RaceStatusEnum } from "@tvg/types/Race";
import buildRaceUrl from "@tvg/formatter/url";
import { formatPostTime } from "@tvg/formatter/dates";
import type { HomepageTrackListRace } from "@tvg/types/HompeageTrackList";
import Icon from "@fdr/static-ui/Icons";
import { playCircle } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { useNewWatchLiveButton } from "@urp/amplitude-experiments";
import Button from "../../_atoms/Button";

import {
  RaceLinkContainer,
  RaceContainer,
  RaceInfo,
  RaceMTP,
  RaceTitle,
  RaceNumber,
  IconContainer,
  PastRaceDate,
  Label
} from "./styled-components";

type ContainerProps = {
  Container: React$ComponentType<*>,
  raceContainerProps: {
    to?: string,
    onClick?: NullaryFn<void>
  }
};

type Props = {
  /**
   * MTP value
   */
  mtp?: number,
  /**
   * Posttime string value
   */
  postTime: string,
  /**
   * Status of the race
   */
  status?: RaceStatusEnum,
  /**
   * Track name
   */
  trackName: string,
  /**
   * Track short name
   */
  trackShortName?: string,
  /**
   * Track abbreviation
   */
  trackAbbr?: string,
  /**
   * Race number
   */
  raceNumber: string,
  /**
   * Defines if it is a link or not
   */
  isLink: boolean,
  /**
   * Whenever to show the race date along side the hour:minutes when mtp > 60
   */
  showRaceDate?: boolean,
  /**
   * When is Highlighted the mtp font-size is bigger
   */
  isMtpHighlightedBellowThreshold?: boolean,
  /**
   * Close modal before link callback
   */
  closePreviousModal?: UnaryFn<mixed, void>,
  /**
   * Handles race click gtm event
   */
  onRaceClickGTM?: UnaryFn<HomepageTrackListRace, void>,
  /**
   * Enables greyhound flag
   */
  isGreyhound?: boolean,
  /**
   * Enables video flag
   */
  hasVideo?: boolean,
  /**
   * SHow a different race for past races
   */
  showPastRace?: boolean,
  /**
   * If bet race link is presented on an area where it's not supposed to be clickable set this to false
   */
  hasHover?: boolean,
  /**
   * open video callback
   */
  onOpenVideo?: BinaryFn<string, string, void>,
  /**
   * Toggles watch video on my bets
   */
  watchVideoActive?: boolean,
  /**
   * For single race "My bets" modals, the race info is sticky
   */
  isRaceContainerSticky?: boolean,
  /**
   * feature toggle active bets
   */
  useNewProgramPageLayout?: boolean,
  /*
   * Show bottom border
   */
  hasBottomBorder?: boolean,
  /**
   * Is future bets tab active
   */
  isFutureBetsTab?: boolean
};

const BetRaceLink = ({
  mtp = 0,
  postTime = "",
  status = "O",
  trackAbbr = "",
  trackName = "",
  trackShortName = "",
  raceNumber = "1",
  isLink = true,
  showRaceDate = false,
  isMtpHighlightedBellowThreshold = true,
  onRaceClickGTM = noop,
  closePreviousModal = noop,
  isGreyhound = false,
  hasVideo = false,
  showPastRace = false,
  hasHover = true,
  onOpenVideo = noop,
  watchVideoActive = false,
  isRaceContainerSticky = false,
  useNewProgramPageLayout = false,
  hasBottomBorder = false,
  isFutureBetsTab = true
}: Props) => {
  const isExperimentActive = useNewWatchLiveButton();

  const renderLegacy = ({ Container, raceContainerProps }: ContainerProps) => {
    return (
      <Container
        {...raceContainerProps}
        hasBottomBorder={hasBottomBorder}
        onTouchStart={noop}
        hasHover={hasHover}
        isStickyToTop={isRaceContainerSticky}
      >
        <RaceInfo>
          {!showPastRace ? (
            <RaceMTP
              mtp={mtp}
              postTime={postTime}
              status={status}
              showRaceDate={showRaceDate}
              multipleLine
              isHighlightedBellowThreshold={isMtpHighlightedBellowThreshold}
            />
          ) : (
            <PastRaceDate>{formatPostTime(postTime, true)}</PastRaceDate>
          )}
          <RaceTitle>{trackShortName || trackName}</RaceTitle>
          <RaceNumber>Race {raceNumber}</RaceNumber>
        </RaceInfo>
        {hasVideo && watchVideoActive && (
          <Button
            type="secondary"
            onClick={() =>
              onOpenVideo(
                `${trackName} R${raceNumber}`,
                buildRaceUrl(trackAbbr, trackName, raceNumber)
              )
            }
            qaLabel="betRaceLink-watchBtn"
            size="medium"
          >
            <IconContainer hasExperiment={isExperimentActive}>
              <Icon
                icon={playCircle}
                size={16}
                qaLabel="watchIcon"
                color={buildColor("blue", "500")}
              />
              Watch
              {isExperimentActive && <Label>live</Label>}
            </IconContainer>
          </Button>
        )}
      </Container>
    );
  };

  const renderNewLayout = ({
    Container,
    raceContainerProps
  }: ContainerProps) => {
    return (
      <Container
        {...raceContainerProps}
        hasBottomBorder={hasBottomBorder}
        onTouchStart={noop}
        hasHover={hasHover}
        isStickyToTop={isRaceContainerSticky}
      >
        <RaceInfo useNewProgramPageLayout>
          <RaceTitle>{trackShortName || trackName}</RaceTitle>
          <RaceNumber>Race {raceNumber}</RaceNumber>
          <RaceMTP
            isNewLayout
            isSmall
            showDescription={false}
            showPostTime={!showPastRace}
            mode="light"
            mtp={mtp}
            postTime={postTime}
            status={status}
            showRaceDate={showRaceDate}
            isHighlightedBellowThreshold={isMtpHighlightedBellowThreshold}
            isFutureBetsTab={isFutureBetsTab}
          />
        </RaceInfo>
        {hasVideo && watchVideoActive && (
          <Button
            type="tertiary"
            onClick={() =>
              onOpenVideo(
                `${trackName} R${raceNumber}`,
                buildRaceUrl(trackAbbr, trackName, raceNumber)
              )
            }
            qaLabel="betRaceLink-watchBtn"
            size="watch"
          >
            <IconContainer hasExperiment={isExperimentActive}>
              <Icon
                icon={playCircle}
                size={17}
                qaLabel="watchIcon"
                color={buildColor("blue", "500")}
              />
              Watch
              {isExperimentActive && <Label>live</Label>}
            </IconContainer>
          </Button>
        )}
      </Container>
    );
  };

  const Container = isLink ? RaceLinkContainer : RaceContainer;

  const url = buildRaceUrl(trackAbbr, trackName, raceNumber);

  const gtmNavigationData = {
    trackName,
    number: raceNumber,
    mtp,
    isGreyhound,
    url
  };

  const handleRaceContainerClick = () => {
    onRaceClickGTM(gtmNavigationData);
    closePreviousModal(gtmNavigationData);
  };

  const raceContainerProps = isLink
    ? {
        to: buildRaceUrl(trackAbbr, trackName, raceNumber),
        onClick: () => handleRaceContainerClick(),
        "data-qa-label": "betRaceLink"
      }
    : {
        "data-qa-label": "betRace"
      };

  return useNewProgramPageLayout
    ? renderNewLayout({
        Container,
        raceContainerProps
      })
    : renderLegacy({
        Container,
        raceContainerProps
      });
};

export default BetRaceLink;
